import { ref, reactive, toRef } from 'vue';
import { BuniChatWidget } from '@buni.ai/chatbot-core';
import type {
  BuniChatOptions,
  BuniChatState,
  CustomerData,
  SessionVariables
} from '@buni.ai/chatbot-core';

// The chat widget is a single floating overlay for the whole page, so the
// underlying BuniChatWidget instance and its reactive state are shared
// module-level singletons. Every useBuniChat() call — regardless of which
// component initialized the widget — observes and controls the same widget,
// via refs (toRef below) that proxy through to this shared state rather than
// snapshotting its value at call time.
let widgetInstance: BuniChatWidget | null = null;
const state = reactive<BuniChatState>({
  isOpen: false,
  isLoaded: false,
  isMinimized: false,
  unreadCount: 0,
});
const isReady = ref(false);

function attachWidgetListeners(widget: BuniChatWidget) {
  widget.on('connection_ready', () => {
    isReady.value = true;
  });

  widget.on('visibility_changed', (data: any) => {
    state.isOpen = data.visibility === 'visible';
  });

  widget.on('ready', () => {
    state.isLoaded = true;
  });

  widget.on('minimized', () => {
    state.isMinimized = true;
  });

  widget.on('maximized', () => {
    state.isMinimized = false;
  });

  widget.on('new_message', (data: any) => {
    if (data.isFromBot) {
      state.unreadCount++;
    }
  });
}

export function useBuniChat(options?: BuniChatOptions) {
  const initialize = async (initOptions: BuniChatOptions) => {
    if (!widgetInstance) {
      widgetInstance = new BuniChatWidget();
      attachWidgetListeners(widgetInstance);
    }

    await widgetInstance.initialize(initOptions);
  };

  // Auto-initialize if options provided
  if (options) {
    initialize(options);
  }

  return {
    // Reactive state — toRef keeps these in sync with the shared `state`/
    // `isReady` singletons, unlike a plain ref(state.x) snapshot which would
    // freeze at whatever value state.x held when useBuniChat() was called.
    state,
    isOpen: toRef(state, 'isOpen'),
    isLoaded: toRef(state, 'isLoaded'),
    isMinimized: toRef(state, 'isMinimized'),
    unreadCount: toRef(state, 'unreadCount'),
    isReady,

    // Methods
    initialize,
    show: () => widgetInstance?.show(),
    hide: () => widgetInstance?.hide(),
    toggle: () => widgetInstance?.toggle(),
    minimize: () => widgetInstance?.minimize(),
    maximize: () => widgetInstance?.maximize(),
    destroy: () => {
      widgetInstance?.destroy();
      widgetInstance = null;
      isReady.value = false;
      state.isOpen = false;
      state.isLoaded = false;
      state.isMinimized = false;
      state.unreadCount = 0;
    },

    // Data management
    setCustomerData: (data: CustomerData) => widgetInstance?.setCustomerData(data),
    getCustomerData: () => widgetInstance?.getCustomerData(),
    setSessionVariables: (variables: SessionVariables) => widgetInstance?.setSessionVariables(variables),
    getSessionVariables: () => widgetInstance?.getSessionVariables(),
    
    // Chat actions
    sendMessage: (message: string) => widgetInstance?.sendMessage(message),
    clearChat: () => widgetInstance?.clearChat(),
    
    // Theme management
    setTheme: (theme: "light" | "dark" | "system") => widgetInstance?.setTheme(theme),
    open: () => widgetInstance?.open(),
    close: () => widgetInstance?.close(),
  };
}